/**
 * Interface primitives.
 *
 * Small and unabstracted on purpose: the styling lives in styles.css against
 * semantic tokens, so these components are mostly about behaviour — focus
 * handling, the escape key, an icon that animates with its button.
 */

import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import {
  Icon,
  IconHover,
  LoaderCircleIcon,
  TriangleAlertIcon,
  XIcon,
  type AnimatedIcon,
} from './icons';

type ButtonVariant = 'primary' | 'secondary' | 'ghost';

export function Button({
  variant = 'secondary',
  icon,
  small,
  busy,
  children,
  className = '',
  ...rest
}: {
  variant?: ButtonVariant;
  icon?: AnimatedIcon;
  small?: boolean;
  busy?: boolean;
  children?: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  const classes = ['btn', `btn-${variant}`, small ? 'btn-sm' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <IconHover
      as="button"
      className={classes}
      disabled={busy || rest.disabled}
      {...rest}
    >
      {busy ? (
        <span className="animate-spin">
          <Icon icon={LoaderCircleIcon} size={small ? 14 : 16} isAnimated={false} />
        </span>
      ) : icon ? (
        <Icon icon={icon} size={small ? 14 : 16} />
      ) : null}
      {children}
    </IconHover>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: ReactNode;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
      {error ? (
        <div className="mt-1.5 flex items-start gap-1.5 text-[13px] text-refusal">
          <span className="mt-0.5 shrink-0">
            <Icon icon={TriangleAlertIcon} size={13} isAnimated={false} />
          </span>
          {error}
        </div>
      ) : hint ? (
        <p className="mt-1.5 text-[13px] text-faint">{hint}</p>
      ) : null}
    </div>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`input ${props.className ?? ''}`} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`textarea ${props.className ?? ''}`} />;
}

export function Dialog({
  title,
  description,
  onClose,
  children,
  footer,
}: {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Focus moves into the dialog so the keyboard does not stay behind it.
    panel.current?.querySelector<HTMLElement>('input, textarea, button')?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="dialog" ref={panel} role="dialog" aria-modal="true" aria-label={title}>
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div>
            <h2 className="text-[15px] font-medium">{title}</h2>
            {description ? <p className="mt-0.5 text-[13px] text-muted">{description}</p> : null}
          </div>
          <Button variant="ghost" icon={XIcon} onClick={onClose} aria-label="Close" small />
        </div>
        <div className="px-5 py-4">{children}</div>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3.5">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: AnimatedIcon;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="card flex flex-col items-center px-6 py-14 text-center">
      <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-sunken text-muted">
        <Icon icon={icon} size={18} />
      </span>
      <h3 className="text-[15px] font-medium">{title}</h3>
      {children ? (
        <p className="mt-1.5 max-w-md text-[13.5px] leading-relaxed text-muted">{children}</p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function Alert({ children, tone = 'refusal' }: { children: ReactNode; tone?: 'refusal' | 'warn' }) {
  const styles =
    tone === 'refusal'
      ? 'bg-refusal-wash text-refusal'
      : 'bg-warn-wash text-warn';
  return (
    <div className={`flex items-start gap-2 rounded-lg px-3 py-2.5 text-[13.5px] ${styles}`}>
      <span className="mt-0.5 shrink-0">
        <Icon icon={TriangleAlertIcon} size={14} isAnimated={false} />
      </span>
      <div>{children}</div>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-[22px]">{title}</h1>
        {description ? (
          <p className="mt-1 max-w-2xl text-[13.5px] leading-relaxed text-muted">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function Spinner({ size = 16 }: { size?: number }) {
  return (
    <span className="inline-flex animate-spin text-muted">
      <Icon icon={LoaderCircleIcon} size={size} isAnimated={false} />
    </span>
  );
}

export function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
